/* eslint-disable complexity */
import React from "react";
import { forwardRef } from "react";
import type {
  ObsValue,
  ObservationTableSection,
  TableSections,
} from "@/components/ObservationChart";
import ObservationSection from "@/components/ObservationChart/ObservationSection";
import { css } from "@/theme";
import type { Observation, ObservationNeuroMovementArm, ObservationNeuroMovementLeg } from "@/resources/types";
import {
  armMovementLabels,
  legMovementLabels,
} from "@/resources/types";
import {
  getValueFromObs,
  type GetValueFromObsProps,
} from "@/components/ObservationSummary";
import { ObsTabs } from "./ObservationTabs_ctx";
import ObservationChartHeaderRow from "./ObservationChartHeaderRow";
import { get, set } from "lodash";
import { obsMetricNames } from "@/resources/observations_metricUtils";
import { getVitalsSignsTableSections } from "./index";

const styles = {
  table: css`
    border-spacing: 0;
    border-collapse: collapse;
    text-align: left;
    color: black;
    font-size: 14px;
    width: 100%;

    & tr td {
      min-width: 60px;
      padding: 0.2rem;
    }
  `,
  table_container: css`
    @media print {
      padding: 3rem;

      html,
      body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      table {
        border-spacing: 0;
        border-collapse: collapse;
        text-align: center;
        color: black;
        font-size: 0.7rem;
        width: 100%;

        & tr td {
          border: 1px solid #c3c8de;
          min-width: 70px;
          padding: 0.16rem;
        }

        & tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
      }
    }
  `,
};

const NUM_CELLS = 14;

// These values are converted to strings
const BEDSIDE_BLOOD_PRES_KEYS = [
  "bedsideBloodPressureLaying",
  "bedsideBloodPressureLeftArm",
  "bedsideBloodPressureRightArm",
  "bedsideBloodPressureStanding",
];

const renderBlankCells = (count: number) =>
  count > 0
    ? [...Array(count).keys()].map((num) => <td key={num}></td>)
    : undefined;

interface ObservationProps {
  observations: Observation[];
  tableSections: TableSections;
  type: ObsTabs;
  encounterId: string;
  onEditObservation?: (observation: Observation) => void;
}

const ObservationTable = forwardRef<HTMLDivElement, ObservationProps>((props, ref) => {
  const { observations: observationsTmp, tableSections, type, encounterId, onEditObservation } = props;

  // Create a map of original observations by ID for lookup when editing
  const originalObservationsById = React.useMemo(() => {
    return new Map(observationsTmp.map(obs => [obs.id, obs]));
  }, [observationsTmp]);

  // Wrapper to pass original observation to onEditObservation callback
  const handleEditObservation = React.useCallback((mutatedObservation: Observation) => {
    if (onEditObservation) {
      // Look up the original observation by ID to avoid sending mutated values
      const originalObservation = originalObservationsById.get(mutatedObservation.id);
      onEditObservation(originalObservation || mutatedObservation);
    }
  }, [onEditObservation, originalObservationsById]);

  const observations = React.useMemo(() => {
    // Filter out invalid observations
    const validObservations = observationsTmp.filter((obs) => !obs.invalid);

    // add up the total glasgow score
    return validObservations.map((obs) => {
      // Create a shallow copy to avoid mutating the original observation
      const obsCopy = { ...obs };
      if (obsCopy.neuro) {
        // Create a copy of neuro object if it exists
        obsCopy.neuro = { ...obsCopy.neuro };
        if (obsCopy.neuro.movement) {
          obsCopy.neuro.movement = { ...obsCopy.neuro.movement };
        }
        if (obsCopy.neuro.limbNeuro) {
          obsCopy.neuro.limbNeuro = { ...obsCopy.neuro.limbNeuro };
        }
      }
      if (obsCopy.neuro) {
        const neuro = obsCopy.neuro as any;
        const gasglowDefined = neuro.eyeOpening || neuro.verbal || neuro.motor;
        if (gasglowDefined) {
          let glasgowTotal = "N/A" as number | string;
          if (neuro.eyeOpening === "C" || neuro.verbal === "T") {
            glasgowTotal = "N/A";
          } else {
            glasgowTotal = 0;
            if (neuro.eyeOpening && typeof neuro.eyeOpening === "number" && !isNaN(neuro.eyeOpening)) {
              glasgowTotal += neuro.eyeOpening;
            }
            if (neuro.verbal && typeof neuro.verbal === "number" && !isNaN(neuro.verbal)) {
              glasgowTotal += neuro.verbal;
            }
            if (neuro.motor && typeof neuro.motor === "number" && !isNaN(neuro.motor)) {
              glasgowTotal += neuro.motor;
            }
          }
          neuro.glasgowTotal = glasgowTotal;
        }
      }
      if (type === "neurovascular") {
        // @ts-ignore
        if (Object.keys(obsCopy.neuro).length === 0) return;
        /// @ts-ignore
        else if (Object.keys(obsCopy.neuro).length === 1 && obsCopy.neuro?.glasgowScore && obsCopy.neuro.glasgowScore === "NA") {
          return;
        }
      }
      // replace "dopler" with "Doppler only" in obsMetricNames.limbNeuroRightArmPulse, obsMetricNames.limbNeuroLeftArmPulse, obsMetricNames.limbNeuroRightLegPulse, obsMetricNames.limbNeuroLeftLegPulse
      const replaceDoppler = (obsin: Observation, keys: string[]) => {
        for (const key of keys) {
          if (get(obsin, key) === "doppler") {
           // set(obsin, key, "Doppler only");
          }
        }
      };
      replaceDoppler(obsCopy, [
        obsMetricNames.limbNeuroRightArmPulse,
        obsMetricNames.limbNeuroLeftArmPulse,
        obsMetricNames.limbNeuroRightLegPulse,
        obsMetricNames.limbNeuroLeftLegPulse,
      ]);

      // Map limb movement values to their display labels using armMovementLabels and legMovementLabels
      // If label is undefined (bad data), keep the raw value as-is
      const mapLimbMovementLabels = (obsin: Observation, armKeys: string[], legKeys: string[]) => {
        for (const key of armKeys) {
          const value = get(obsin, key) as ObservationNeuroMovementArm | undefined;
          const label = value ? armMovementLabels[value] : undefined;
          if (label) {
            set(obsin, key, label.toLowerCase());
          }
          // If label is undefined, the raw value remains unchanged
        }
        for (const key of legKeys) {
          const value = get(obsin, key) as ObservationNeuroMovementLeg | undefined;
          const label = value ? legMovementLabels[value] : undefined;
          if (label) {
            set(obsin, key, label.toLowerCase());
          }
          // If label is undefined, the raw value remains unchanged
        }
      };
      mapLimbMovementLabels(obsCopy, [
        obsMetricNames.limbMovRightArm,
        obsMetricNames.limbMovLeftArm,
      ], [
        obsMetricNames.limbMovRightLeg,
        obsMetricNames.limbMovLeftLeg,
      ]);
      return obsCopy;
    }).filter((x: Observation | undefined) => x) as Observation[];

  }, [observationsTmp, type]);

  const processedTableSections = React.useMemo(() => {
    if (type === ObsTabs.VITALS_SIGNS) {
      return getVitalsSignsTableSections(observations as Observation[]);
    }
    return tableSections;
  }, [type, tableSections, observations]);

  const blankCells = renderBlankCells(NUM_CELLS - observations.length);

  const makeValues = (section: ObservationTableSection) => {
    return observations.map((obs) => {
      const val = getValueFromObs({
        observation: obs,
        key: section.dataKey,
        type,
      } as GetValueFromObsProps);

      const isBedBloodPres = BEDSIDE_BLOOD_PRES_KEYS.includes(section.dataKey);

      // @ts-ignore `systolic`/`diastolic` keys are defined for
      // bedside blood pressure
      if (isBedBloodPres && val && val.systolic && val.diastolic) {
        // @ts-ignore See above
        return `${val.systolic}/${val.diastolic}`;
      }
      if (section.dataKey === "riskScore") {
        // JamesRippey asked that incomplete NEWS2 scores not be shown
        // @ts-ignore
        if (val?.score && !val.incomplete) return val.score;
        return "";
      }
      if (section.dataKey === obsMetricNames.hypercapnicRespiratoryFailure) {
        return typeof val === "boolean" ? (val ? "Y" : "N") : val;
      }

      return val as ObsValue;
    });
  };

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div css={styles.table_container} ref={ref}>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <table css={styles.table}>
        <tbody>
          <ObservationChartHeaderRow
            name="User stamp"
            // @ts-ignore
            values={observations}
            blankCells={blankCells}
            tableSections={processedTableSections}
            encounterId={encounterId}
            onEditObservation={handleEditObservation}
          />

          {processedTableSections.map((section) => (
            <ObservationSection
              key={section.dataKey}
              section={section}
              values={makeValues(section)}
              blankCells={blankCells}
              tableSections={processedTableSections}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});

ObservationTable.displayName = "ObservationTable";

export default ObservationTable;
